import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePOS } from "@/context/POSContext";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import DrillDialog from "@/components/dashboard/DrillDialog";
import AttentionStrip from "@/components/dashboard/AttentionStrip";
import ExecutivePulseCards from "@/components/dashboard/ExecutivePulseCards";
import FinancialWaterfallWidget from "@/components/dashboard/FinancialWaterfallWidget";
import LiveOpsStrip from "@/components/dashboard/LiveOpsStrip";
import DualCashWidget from "@/components/dashboard/DualCashWidget";
import MenuEngineeringWidget from "@/components/dashboard/MenuEngineeringWidget";
import SalesTargetModal from "@/components/dashboard/SalesTargetModal";
import AggregatorImportModal from "@/components/dashboard/AggregatorImportModal";
import CashCountHistoryModal from "@/components/dashboard/CashCountHistoryModal";
import {
  ItemsPanel, LEAK_LABEL, LeakagePanel, LiveStrip, OrderTypesPanel, OutflowPanel, TakingsStrip, TrendPanel,
} from "@/components/dashboard/Panels";
import { getDashboard, getLeakageRows, getOutflowRows } from "@/lib/dashboardApi";

const REFRESH_MS = 60_000;
const REALTIME_DEBOUNCE_MS = 3_000;
const OUTFLOW_LABEL = { expenses: "Expenses", withdrawals: "Withdrawals", top_up: "Cash top-up" };

/** The failure in the owner's words, not axios's ("status code 504"). */
export const errorText = err => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  const status = err?.response?.status;
  if (!err?.response || [502, 503, 504].includes(status)) return "the server is not answering. Check that the server PC is on and connected";
  if (status === 401) return "your sign-in has expired. Sign in again";
  if (status === 403) return "this account is not allowed to see the owner dashboard";
  return err?.message || "unknown error";
};

/**
 * RestoYaar Owner Dashboard Command Centre v2.3
 * Provides 10-15s executive clarity:
 * - Stateful Attention Required incident FSM
 * - Executive Pulse KPI Cards (Invoiced Sales, Collections, Margin, Labour, Net Cashflow)
 * - Headline takings & live running board
 * - Financial Waterfall & Tender Allocation reconciliation
 * - Live Station & Kitchen KOT delay monitoring
 * - Dual Till Drawer & Safe cash accounting
 * - BCG Menu Engineering Matrix with recipe cost coverage badge
 * - Historical trend, leakage, items, and outflow drilldowns
 */
export default function DashboardPage() {
  const { token, subscribeRealtime, settings } = usePOS();
  const [params] = useSearchParams();
  // The Telegram day-end message links here with ?date=YYYY-MM-DD.
  const linked = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") || "") ? params.get("date") : null;
  const [period, setPeriod] = useState(
    linked ? { preset: "custom", from: linked, to: linked } : { preset: "today", from: "", to: "" },
  );
  const [itemsBy, setItemsBy] = useState("qty");
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [drill, setDrill] = useState(null);

  // Modals state
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showAggregatorModal, setShowAggregatorModal] = useState(false);
  const [showCountHistoryModal, setShowCountHistoryModal] = useState(false);

  const latest = useRef(0);

  const query = period.preset === "today" ? {} : { from: period.from, to: period.to };

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const body = await getDashboard({ ...query, itemsBy }, token);
      if (ticket !== latest.current) return;
      setData(body);
      setFetchError(null);
      setUpdatedAt(new Date());
    } catch (err) {
      if (ticket === latest.current) setFetchError(errorText(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, itemsBy, period.preset, period.from, period.to]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (typeof subscribeRealtime !== "function") return undefined;
    let pending = null;
    const unsubscribe = subscribeRealtime(event => {
      if (event?.type !== "order_update" && event?.type !== "resync") return;
      clearTimeout(pending);
      pending = setTimeout(load, REALTIME_DEBOUNCE_MS);
    });
    return () => { clearTimeout(pending); if (typeof unsubscribe === "function") unsubscribe(); };
  }, [load, subscribeRealtime]);

  const drillPeriod = data ? { from: data.from_date, to: data.to_date } : query;
  const openLeak = line => setDrill({
    title: `Leakage — ${LEAK_LABEL[line] || line}`,
    load: () => getLeakageRows(line, drillPeriod, token),
  });
  const openOutflow = kind => setDrill({
    title: OUTFLOW_LABEL[kind] || kind,
    load: () => getOutflowRows(kind, drillPeriod, token),
  });

  const panel = key => {
    const value = data?.[key];
    return value && value.error ? { data: null, error: value.error } : { data: value, error: null };
  };

  const sales = panel("sales");
  const leakage = panel("leakage");
  const items = panel("items");
  const outflow = panel("outflow");
  const waterfall = panel("waterfall");
  const cashRecon = panel("cash_reconciliation");
  const kitchenOps = panel("kitchen_ops");
  const labourPulse = panel("labour_pulse");
  const menuEngineering = panel("menu_engineering");

  const singleDay = data ? data.from_date === data.to_date : true;

  const periodText = (() => {
    if (!data) return "";
    const fmt = iso => new Date(`${iso}T00:00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
    if (data.from_date === data.to_date) {
      return data.from_date === data.business_today ? `Today, ${fmt(data.from_date)}` : fmt(data.from_date);
    }
    return `${fmt(data.from_date)} to ${fmt(data.to_date)}`;
  })();

  const isTodayOrCurrent = data ? (data.to_date >= data.business_today && data.from_date <= data.business_today) : false;

  return (
    <div className="p-loose lg:px-section space-y-loose" data-testid="owner-dashboard">
      {/* Top Header & Range Bar */}
      <div className="flex flex-wrap items-end justify-between gap-snug">
        <div>
          <h1 className="sr-only">Owner dashboard</h1>
          <p className="text-body text-muted-foreground" data-testid="dash-period">
            {periodText || "Loading…"}
            {updatedAt && <span> — updated {updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
          </p>
        </div>
        <DateRangePicker
          value={{ from: data?.from_date || period.from, to: data?.to_date || period.to }}
          preset={period.preset}
          businessDayStart={settings?.business_day_start}
          onChange={next => {
            setData(null);
            setPeriod({ preset: next.preset || "custom", from: next.from, to: next.to || next.from });
          }}
          testId="dash-range"
        />
      </div>

      {fetchError && (
        <div className="flex flex-wrap items-center justify-between gap-snug rounded-md bg-danger-subtle px-base py-snug" role="alert" data-testid="dash-fetch-error">
          <p className="text-body text-danger">
            Owner dashboard not updated — {fetchError}. {data ? "These are the last numbers it had. " : ""}Retrying on its own every minute.
          </p>
          <Button variant="outline" size="sm" onClick={load} data-testid="dash-retry">Retry now</Button>
        </div>
      )}

      {fetchError && !data ? null : (<>
        {/* 1. Attention Required Strip */}
        <AttentionStrip
          incidents={data?.incidents || []}
          onReload={load}
          token={token}
        />

        {/* 2. Executive Pulse KPI Cards */}
        <ExecutivePulseCards
          waterfall={waterfall.data}
          sales={sales.data}
          targets={data?.targets}
          labourPulse={labourPulse.data}
          menuEngineering={menuEngineering.data}
          outflow={outflow.data}
          onOpenTargetModal={() => setShowTargetModal(true)}
        />

        {/* 3. Takings Headline Strip */}
        <TakingsStrip data={sales.data} error={sales.error} />

        {/* 4. Live Running Board (Active if today is in view) */}
        {data && data.to_date >= data.business_today && data.from_date <= data.business_today && (
          <LiveStrip
            live={data.live}
            netSales={sales.data?.total_sales}
            today={data.from_date === data.business_today && data.to_date === data.business_today}
          />
        )}

        {/* 5. Live Kitchen & Bar Operations */}
        {isTodayOrCurrent && kitchenOps.data && (
          <LiveOpsStrip kitchenOps={kitchenOps.data} />
        )}

        {/* 6. Financial Waterfall & Reconciliation */}
        {waterfall.data && (
          <FinancialWaterfallWidget
            waterfall={waterfall.data}
            onOpenAggregatorModal={() => setShowAggregatorModal(true)}
          />
        )}

        {/* 7. Dual Cash Accounting: Till & Safe */}
        {cashRecon.data && (
          <DualCashWidget
            cashReconciliation={cashRecon.data}
            onOpenCountHistory={() => setShowCountHistoryModal(true)}
          />
        )}

        {/* 8. Menu Engineering BCG Matrix */}
        {menuEngineering.data && (
          <MenuEngineeringWidget menuEngineering={menuEngineering.data} />
        )}

        {/* 9. Deep Analytical Panels (Trends, Order Types, Leakage, Items, Outflow) */}
        <div className="pt-base border-t space-y-loose">
          <div className="grid gap-loose lg:grid-cols-3">
            <TrendPanel data={sales.data} error={sales.error} singleDay={singleDay} />
            <OrderTypesPanel data={sales.data} error={sales.error} singleDay={singleDay} />
          </div>

          <div className="grid gap-loose lg:grid-cols-2 xl:grid-cols-3">
            <LeakagePanel data={leakage.data} error={leakage.error} onOpen={openLeak} />
            <ItemsPanel data={items.data} error={items.error} itemsBy={itemsBy} onItemsBy={setItemsBy} />
            <OutflowPanel data={outflow.data} error={outflow.error} onOpen={openOutflow} />
          </div>
        </div>
      </>)}

      {/* Drill-down Dialog */}
      {drill && <DrillDialog title={drill.title} load={drill.load} onClose={() => setDrill(null)} />}

      {/* Sales Target Modal */}
      <SalesTargetModal
        open={showTargetModal}
        onClose={() => setShowTargetModal(false)}
        token={token}
        onSaved={load}
      />

      {/* Aggregator Reconciliation Import Modal */}
      <AggregatorImportModal
        open={showAggregatorModal}
        onClose={() => setShowAggregatorModal(false)}
        token={token}
        onImported={load}
      />

      {/* Cash Count Audit Trail Modal */}
      <CashCountHistoryModal
        open={showCountHistoryModal}
        onClose={() => setShowCountHistoryModal(false)}
        token={token}
        dayDate={data?.from_date || period.from}
      />
    </div>
  );
}
